import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Users, Plus, Search, Eye, Edit, Trash2, RefreshCw, Power,
  X, Upload, FileText, Download, DollarSign, Briefcase,
  Clock, User, Shield, CheckCircle, Copy, MessageCircle, Send,
  FileCheck, AlertCircle, Loader2, Mail
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Policy options with labels
const POLICY_OPTIONS = {
  working_hours: "Working Hours Policy",
  attendance: "Attendance Policy",
  leave_policy: "Leave Policy",
  code_of_conduct: "Code of Conduct",
  confidentiality: "Confidentiality Agreement",
  dress_code: "Dress Code Policy",
  data_security: "Data Security & IT Policy"
};

// Document type options
const DOCUMENT_TYPES = [
  { value: "handbook", label: "Employee Handbook" },
  { value: "policy", label: "Policy Document" },
  { value: "nda", label: "NDA" },
  { value: "custom", label: "Other Document" }
];

const PreJoiningAdmin = () => {
  const { token } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [activeTab, setActiveTab] = useState("candidates"); // candidates, documents, templates
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showQAModal, setShowQAModal] = useState(false);
  const [showOfferUploadModal, setShowOfferUploadModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [candidatesRes, statsRes, questionsRes, docsRes] = await Promise.all([
        axios.get(`${API}/admin/prejoining/candidates`, authHeaders),
        axios.get(`${API}/admin/prejoining/stats`, authHeaders),
        axios.get(`${API}/admin/prejoining/questions/pending`, authHeaders),
        axios.get(`${API}/admin/prejoining/candidate-documents/pending`, authHeaders)
      ]);
      setCandidates(candidatesRes.data);
      setStats(statsRes.data);
      setPendingQuestions(questionsRes.data);
      setPendingDocs(docsRes.data);
    } catch (err) {
      console.error("Failed to load candidates:", err);
      toast.error("Failed to load candidates");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredCandidates = candidates.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          c.position.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleView = async (candidate) => {
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const res = await axios.get(`${API}/admin/prejoining/candidates/${candidate.id}`, authHeaders);
      setSelectedCandidate(res.data);
      setShowViewModal(true);
    } catch (err) {
      toast.error("Failed to load candidate details");
    }
  };

  const handleEdit = (candidate) => {
    setSelectedCandidate(candidate);
    setEditMode(true);
    setShowAddModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this candidate? This will also delete all their documents and questions.")) return;
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      await axios.delete(`${API}/admin/prejoining/candidates/${id}`, authHeaders);
      setCandidates(candidates.filter(c => c.id !== id));
      toast.success("Candidate deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete candidate");
    }
  };

  const handleToggleAccess = async (candidate) => {
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const res = await axios.post(`${API}/admin/prejoining/candidates/${candidate.id}/toggle-access`, {}, authHeaders);
      setCandidates(candidates.map(c => 
        c.id === candidate.id ? { ...c, access_enabled: res.data.access_enabled } : c
      ));
      toast.success(res.data.access_enabled ? "Access enabled" : "Access disabled");
    } catch (err) {
      toast.error("Failed to toggle access");
    }
  };

  const handleRegenerateCode = async (candidate) => {
    if (!window.confirm("Generate a new access code? The old code will no longer work.")) return;
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const res = await axios.post(`${API}/admin/prejoining/candidates/${candidate.id}/regenerate-code`, {}, authHeaders);
      setCandidates(candidates.map(c => 
        c.id === candidate.id ? { ...c, access_code: res.data.access_code } : c
      ));
      toast.success("New access code generated");
      navigator.clipboard.writeText(res.data.access_code);
      toast.info("Code copied to clipboard");
    } catch (err) {
      toast.error("Failed to regenerate code");
    }
  };

  const handleSendInvitation = async (candidate) => {
    if (!window.confirm(`Send portal invitation email to ${candidate.email}?`)) return;
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      await axios.post(`${API}/admin/prejoining/candidates/${candidate.id}/send-invitation`, {}, authHeaders);
      toast.success(`Invitation sent to ${candidate.email}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invitation");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Access code copied!");
  };

  const getStatusBadge = (status) => {
    const styles = {
      not_opened: "bg-gray-100 text-gray-700",
      viewed: "bg-blue-100 text-blue-700",
      completed: "bg-green-100 text-green-700"
    };
    const labels = {
      not_opened: "Not Opened",
      viewed: "In Progress",
      completed: "Completed"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.not_opened}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getOfferStatusBadge = (candidate) => {
    const status = candidate.offer_status;
    if (!status) return <span className="text-xs text-gray-400">No offer</span>;
    const styles = {
      sent: "bg-yellow-100 text-yellow-700",
      viewed: "bg-blue-100 text-blue-700",
      accepted: "bg-green-100 text-green-700"
    };
    
    if (status === 'accepted' && candidate.offer_accepted_at) {
      // Convert to IST (UTC + 5:30)
      const acceptedDate = new Date(candidate.offer_accepted_at);
      const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds
      const istDate = new Date(acceptedDate.getTime() + istOffset);
      const formattedDate = istDate.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'UTC' // We already added offset, so use UTC to display
      });
      
      return (
        <div className="flex flex-col items-start">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]} flex items-center gap-1`}>
            <CheckCircle className="w-3 h-3" />
            Accepted
          </span>
          <span className="text-[10px] text-green-600 mt-0.5">{formattedDate} IST</span>
        </div>
      );
    }
    
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>{status}</span>;
  };

  const getAckProgress = (candidate) => {
    const enabled = Object.entries(candidate.policies_enabled || {}).filter(([_, v]) => v).length;
    const acked = Object.keys(candidate.policy_acknowledgements || {}).length;
    return `${acked}/${enabled}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="prejoining-admin-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-playfair text-2xl text-charcoal">Pre-Joining Portal</h1>
            <p className="font-dm text-sm text-charcoal-muted mt-1">Manage candidates, offer letters, and documents</p>
          </div>
          <div className="flex gap-3">
            {pendingDocs.length > 0 && (
              <button
                onClick={() => setActiveTab("documents")}
                className="bg-amber-500 hover:bg-amber-600 text-white font-dm font-medium px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <FileCheck className="w-4 h-4" />
                {pendingDocs.length} Docs to Review
              </button>
            )}
            {pendingQuestions.length > 0 && (
              <button
                onClick={() => setShowQAModal(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white font-dm font-medium px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                data-testid="pending-questions-button"
              >
                <MessageCircle className="w-4 h-4" />
                {pendingQuestions.length} Questions
              </button>
            )}
            <button
              onClick={() => { setSelectedCandidate(null); setEditMode(false); setShowAddModal(true); }}
              className="bg-gold hover:bg-gold/90 text-white font-dm font-medium px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              data-testid="add-candidate-button"
            >
              <Plus className="w-4 h-4" />
              Add Candidate
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab("candidates")}
            className={`px-4 py-2 font-dm text-sm border-b-2 transition-colors ${
              activeTab === "candidates" ? "border-gold text-gold" : "border-transparent text-gray-500 hover:text-charcoal"
            }`}
          >
            Candidates
          </button>
          <button
            onClick={() => setActiveTab("documents")}
            className={`px-4 py-2 font-dm text-sm border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === "documents" ? "border-gold text-gold" : "border-transparent text-gray-500 hover:text-charcoal"
            }`}
          >
            Document Review
            {pendingDocs.length > 0 && (
              <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">{pendingDocs.length}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("templates")}
            className={`px-4 py-2 font-dm text-sm border-b-2 transition-colors ${
              activeTab === "templates" ? "border-gold text-gold" : "border-transparent text-gray-500 hover:text-charcoal"
            }`}
          >
            Email Templates
          </button>
        </div>

        {/* Stats Cards */}
        {activeTab === "candidates" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg border border-gray-100 p-4">
              <p className="font-dm text-sm text-charcoal-muted">Total</p>
              <p className="font-playfair text-2xl text-charcoal">{stats.total}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-100 p-4">
              <p className="font-dm text-sm text-charcoal-muted">Not Opened</p>
              <p className="font-playfair text-2xl text-gray-600">{stats.not_opened}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-100 p-4">
              <p className="font-dm text-sm text-charcoal-muted">In Progress</p>
              <p className="font-playfair text-2xl text-blue-600">{stats.viewed}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-100 p-4">
              <p className="font-dm text-sm text-charcoal-muted">Completed</p>
              <p className="font-playfair text-2xl text-green-600">{stats.completed}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-100 p-4">
              <p className="font-dm text-sm text-charcoal-muted">Active Access</p>
              <p className="font-playfair text-2xl text-gold">{stats.active}</p>
            </div>
          </div>
        )}
      </div>

      {/* Candidates Tab */}
      {activeTab === "candidates" && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-lg border border-gray-100 p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, or position..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                  data-testid="candidate-search-input"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                data-testid="candidate-status-filter"
              >
                <option value="all">All Status</option>
                <option value="not_opened">Not Opened</option>
                <option value="viewed">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          {/* Candidates Table */}
          <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-dm text-xs font-medium text-charcoal-muted uppercase tracking-wide">Candidate</th>
                    <th className="px-4 py-3 text-left font-dm text-xs font-medium text-charcoal-muted uppercase tracking-wide">Position</th>
                    <th className="px-4 py-3 text-left font-dm text-xs font-medium text-charcoal-muted uppercase tracking-wide">Access Code</th>
                    <th className="px-4 py-3 text-left font-dm text-xs font-medium text-charcoal-muted uppercase tracking-wide">Offer</th>
                    <th className="px-4 py-3 text-left font-dm text-xs font-medium text-charcoal-muted uppercase tracking-wide">Policies</th>
                    <th className="px-4 py-3 text-left font-dm text-xs font-medium text-charcoal-muted uppercase tracking-wide">Status</th>
                    <th className="px-4 py-3 text-right font-dm text-xs font-medium text-charcoal-muted uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCandidates.map((candidate) => (
                    <tr key={candidate.id} className="hover:bg-gray-50" data-testid={`candidate-row-${candidate.id}`}>
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-dm text-sm text-charcoal font-medium">{candidate.name}</p>
                          <p className="font-dm text-xs text-charcoal-muted">{candidate.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4 font-dm text-sm text-charcoal">{candidate.position}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">{candidate.access_code}</code>
                          <button onClick={() => copyCode(candidate.access_code)} className="text-gray-400 hover:text-gold">
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4">{getOfferStatusBadge(candidate)}</td>
                      <td className="px-4 py-4">
                        <span className="font-dm text-sm text-charcoal">{getAckProgress(candidate)}</span>
                      </td>
                      <td className="px-4 py-4">{getStatusBadge(candidate.status)}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button 
                            onClick={() => handleSendInvitation(candidate)} 
                            className={`p-1.5 rounded ${candidate.invitation_sent ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}
                            title={candidate.invitation_sent ? "Resend Invitation" : "Send Invitation Email"}
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => { setSelectedCandidate(candidate); setShowOfferUploadModal(true); }} 
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded" 
                            title="Upload Offer Letter"
                          >
                            <Upload className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleView(candidate)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="View Details">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleEdit(candidate)} className="p-1.5 text-gray-400 hover:text-gold hover:bg-gold/10 rounded" title="Edit">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleRegenerateCode(candidate)} className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded" title="Regenerate Code">
                            <RefreshCw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => { setSelectedCandidate(candidate); setShowDocModal(true); }}
                            className="p-1.5 text-gray-400 hover:text-navy hover:bg-navy/10 rounded"
                            title="Manage Documents"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleAccess(candidate)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              candidate.access_enabled 
                                ? 'text-green-600 hover:bg-green-50' 
                                : 'text-red-600 hover:bg-red-50'
                            }`}
                            title={candidate.access_enabled ? "Disable Access" : "Enable Access"}
                          >
                            <Power className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(candidate.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredCandidates.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="font-dm text-charcoal-muted">No candidates found</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Document Review Tab */}
      {activeTab === "documents" && (
        <CandidateDocumentsReview token={token} pendingDocs={pendingDocs} onUpdate={fetchData} />
      )}

      {/* Email Templates Tab */}
      {activeTab === "templates" && (
        <EmailTemplatesManager token={token} />
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddEditCandidateModal
            candidate={editMode ? selectedCandidate : null}
            onClose={() => { setShowAddModal(false); setEditMode(false); setSelectedCandidate(null); }}
            onSuccess={() => { fetchData(); setShowAddModal(false); setEditMode(false); setSelectedCandidate(null); }}
            token={token}
          />
        )}
      </AnimatePresence>

      {/* View Modal */}
      <AnimatePresence>
        {showViewModal && selectedCandidate && (
          <ViewCandidateModal
            candidate={selectedCandidate}
            onClose={() => { setShowViewModal(false); setSelectedCandidate(null); }}
            token={token}
            onReply={fetchData}
          />
        )}
      </AnimatePresence>

      {/* Document Modal */}
      <AnimatePresence>
        {showDocModal && selectedCandidate && (
          <DocumentModal
            candidate={selectedCandidate}
            onClose={() => { setShowDocModal(false); setSelectedCandidate(null); }}
            token={token}
          />
        )}
      </AnimatePresence>

      {/* Pending Questions Modal */}
      <AnimatePresence>
        {showQAModal && (
          <PendingQuestionsModal
            questions={pendingQuestions}
            onClose={() => setShowQAModal(false)}
            token={token}
            onReply={fetchData}
          />
        )}
      </AnimatePresence>

      {/* Offer Letter Upload Modal */}
      <AnimatePresence>
        {showOfferUploadModal && selectedCandidate && (
          <OfferLetterUploadModal
            candidate={selectedCandidate}
            onClose={() => { setShowOfferUploadModal(false); setSelectedCandidate(null); }}
            token={token}
            onSuccess={fetchData}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Add/Edit Candidate Modal
const AddEditCandidateModal = ({ candidate, onClose, onSuccess, token }) => {
  const isEdit = !!candidate;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: candidate?.name || "",
    email: candidate?.email || "",
    position: candidate?.position || "",
    salary: candidate?.salary || "",
    probation_period: candidate?.probation_period || "3 months",
    reporting_manager: candidate?.reporting_manager || "",
    notes: candidate?.notes || "",
    document_checklist: candidate?.document_checklist || ["Aadhaar Card", "PAN Card", "Educational Certificates", "Passport Size Photo", "Address Proof"],
    policies_enabled: candidate?.policies_enabled || {
      working_hours: true,
      attendance: true,
      leave_policy: true,
      code_of_conduct: true,
      confidentiality: true,
      dress_code: true,
      data_security: true
    }
  });
  const [newChecklistItem, setNewChecklistItem] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit) {
        await axios.put(`${API}/admin/prejoining/candidates/${candidate.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Candidate updated successfully");
      } else {
        const res = await axios.post(`${API}/admin/prejoining/candidates`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Candidate created! Access Code: ${res.data.access_code}`);
        navigator.clipboard.writeText(res.data.access_code);
        toast.info("Access code copied to clipboard");
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save candidate");
    } finally {
      setLoading(false);
    }
  };

  const addChecklistItem = () => {
    if (newChecklistItem.trim() && !formData.document_checklist.includes(newChecklistItem.trim())) {
      setFormData({
        ...formData,
        document_checklist: [...formData.document_checklist, newChecklistItem.trim()]
      });
      setNewChecklistItem("");
    }
  };

  const removeChecklistItem = (item) => {
    setFormData({
      ...formData,
      document_checklist: formData.document_checklist.filter(i => i !== item)
    });
  };

  const togglePolicy = (key) => {
    setFormData({
      ...formData,
      policies_enabled: {
        ...formData.policies_enabled,
        [key]: !formData.policies_enabled[key]
      }
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-navy text-white px-6 py-4 flex items-center justify-between">
          <h2 className="font-playfair text-xl">{isEdit ? "Edit Candidate" : "Add New Candidate"}</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="space-y-6">
            {/* Basic Info */}
            <div>
              <h3 className="font-dm font-medium text-charcoal mb-4 flex items-center gap-2">
                <User className="w-4 h-4 text-gold" /> Basic Information
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                    required
                    data-testid="candidate-name-input"
                  />
                </div>
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                    required
                    disabled={isEdit}
                    data-testid="candidate-email-input"
                  />
                </div>
              </div>
            </div>

            {/* Employment Details */}
            <div>
              <h3 className="font-dm font-medium text-charcoal mb-4 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-gold" /> Employment Details
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Position *</label>
                  <input
                    type="text"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                    required
                    data-testid="candidate-position-input"
                  />
                </div>
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Salary *</label>
                  <input
                    type="text"
                    value={formData.salary}
                    onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                    placeholder="e.g., ₹5,00,000 per annum"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                    required
                    data-testid="candidate-salary-input"
                  />
                </div>
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Probation Period</label>
                  <select
                    value={formData.probation_period}
                    onChange={(e) => setFormData({ ...formData, probation_period: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                  >
                    <option value="1 month">1 month</option>
                    <option value="3 months">3 months</option>
                    <option value="6 months">6 months</option>
                    <option value="None">None</option>
                  </select>
                </div>
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Reporting Manager</label>
                  <input
                    type="text"
                    value={formData.reporting_manager}
                    onChange={(e) => setFormData({ ...formData, reporting_manager: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block font-dm text-sm text-charcoal mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold resize-none"
                  placeholder="Any additional notes for the candidate..."
                />
              </div>
            </div>

            {/* Policy Toggles */}
            <div>
              <h3 className="font-dm font-medium text-charcoal mb-4 flex items-center gap-2">
                <Shield className="w-4 h-4 text-gold" /> Policies to Show (Candidate must acknowledge each)
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(POLICY_OPTIONS).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={formData.policies_enabled[key]}
                      onChange={() => togglePolicy(key)}
                      className="w-4 h-4 rounded border-gray-300 text-gold focus:ring-gold"
                    />
                    <span className="font-dm text-sm text-charcoal">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Document Checklist */}
            <div>
              <h3 className="font-dm font-medium text-charcoal mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4 text-gold" /> Documents Candidate Should Bring
              </h3>
              <div className="space-y-2 mb-3">
                {formData.document_checklist.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                    <span className="font-dm text-sm text-charcoal flex-1">{item}</span>
                    <button
                      type="button"
                      onClick={() => removeChecklistItem(item)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  placeholder="Add document requirement"
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                  onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addChecklistItem())}
                />
                <button
                  type="button"
                  onClick={addChecklistItem}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-dm text-sm transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-200 rounded-lg font-dm text-sm hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-gold text-white rounded-lg font-dm text-sm hover:bg-gold/90 transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="save-candidate-button"
            >
              {loading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              {isEdit ? "Update Candidate" : "Create Candidate"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// View Candidate Modal
const ViewCandidateModal = ({ candidate, onClose, token, onReply }) => {
  const [replyText, setReplyText] = useState({});
  const [replying, setReplying] = useState({});

  const handleReply = async (questionId) => {
    if (!replyText[questionId]?.trim()) return;
    setReplying({ ...replying, [questionId]: true });
    
    try {
      await axios.post(`${API}/admin/prejoining/questions/${questionId}/reply`, 
        { reply: replyText[questionId] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Reply sent");
      setReplyText({ ...replyText, [questionId]: "" });
      onReply();
    } catch (err) {
      toast.error("Failed to send reply");
    } finally {
      setReplying({ ...replying, [questionId]: false });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-navy text-white px-6 py-4 flex items-center justify-between">
          <h2 className="font-playfair text-xl">Candidate Details</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
              <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center">
                <span className="font-playfair text-2xl text-gold">{candidate.name?.charAt(0)}</span>
              </div>
              <div>
                <h3 className="font-playfair text-xl text-charcoal">{candidate.name}</h3>
                <p className="font-dm text-sm text-charcoal-muted">{candidate.email}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InfoItem icon={Briefcase} label="Position" value={candidate.position} />
              <InfoItem icon={DollarSign} label="Salary" value={candidate.salary} />
              <InfoItem icon={Clock} label="Probation" value={candidate.probation_period} />
              <InfoItem icon={User} label="Reporting To" value={candidate.reporting_manager || "Not specified"} />
            </div>

            {/* Policy Acknowledgements */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-dm font-medium text-charcoal mb-3">Policy Acknowledgements</h4>
              <div className="space-y-2">
                {Object.entries(candidate.policies_enabled || {}).map(([key, enabled]) => {
                  if (!enabled) return null;
                  const ack = candidate.policy_acknowledgements?.[key];
                  return (
                    <div key={key} className="flex items-center justify-between">
                      <span className="font-dm text-sm text-charcoal">{POLICY_OPTIONS[key] || key}</span>
                      {ack?.acknowledged ? (
                        <span className="flex items-center gap-1 text-green-600 text-xs">
                          <CheckCircle className="w-4 h-4" />
                          {new Date(ack.timestamp).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Pending</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Access Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-dm text-xs text-gray-500 uppercase tracking-wide mb-2">Access Code</p>
                <code className="bg-white px-3 py-2 rounded border border-gray-200 font-mono text-lg block text-center">
                  {candidate.access_code}
                </code>
              </div>
              <div className={`p-4 rounded-lg ${candidate.access_enabled ? 'bg-green-50' : 'bg-red-50'}`}>
                <p className="font-dm text-xs uppercase tracking-wide mb-1" style={{ color: candidate.access_enabled ? '#166534' : '#991b1b' }}>Access Status</p>
                <p className="font-dm font-medium text-lg" style={{ color: candidate.access_enabled ? '#166534' : '#991b1b' }}>
                  {candidate.access_enabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
            </div>

            {/* Questions */}
            {candidate.questions && candidate.questions.length > 0 && (
              <div>
                <h4 className="font-dm font-medium text-charcoal mb-3 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-gold" />
                  Questions from Candidate ({candidate.questions.length})
                </h4>
                <div className="space-y-3">
                  {candidate.questions.map((q) => (
                    <div key={q.id} className="border border-gray-200 rounded-lg p-4">
                      <p className="font-dm text-sm text-charcoal mb-2">{q.question}</p>
                      <p className="font-dm text-xs text-gray-500 mb-3">{new Date(q.created_at).toLocaleString()}</p>
                      
                      {q.reply ? (
                        <div className="bg-green-50 rounded-lg p-3 mt-2">
                          <p className="font-dm text-xs text-green-700 mb-1">Reply by {q.replied_by}:</p>
                          <p className="font-dm text-sm text-green-800">{q.reply}</p>
                        </div>
                      ) : (
                        <div className="flex gap-2 mt-2">
                          <input
                            type="text"
                            placeholder="Type your reply..."
                            value={replyText[q.id] || ""}
                            onChange={(e) => setReplyText({ ...replyText, [q.id]: e.target.value })}
                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                          />
                          <button
                            onClick={() => handleReply(q.id)}
                            disabled={replying[q.id]}
                            className="px-4 py-2 bg-gold text-white rounded-lg font-dm text-sm hover:bg-gold/90 disabled:opacity-50"
                          >
                            {replying[q.id] ? "..." : <Send className="w-4 h-4" />}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {candidate.last_login && (
              <p className="font-dm text-xs text-gray-500">
                Last login: {new Date(candidate.last_login).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-gray-400 mt-0.5" />
    <div>
      <p className="font-dm text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="font-dm text-sm text-charcoal">{value}</p>
    </div>
  </div>
);

// Document Management Modal
const DocumentModal = ({ candidate, onClose, token }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [newDoc, setNewDoc] = useState({ file_name: "", file_type: "offer_letter", file: null });

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API}/admin/prejoining/documents/${candidate.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(res.data);
    } catch (err) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!newDoc.file || !newDoc.file_name) {
      toast.error("Please select a file and provide a name");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("candidate_id", candidate.id);
    formData.append("file_name", newDoc.file_name);
    formData.append("file_type", newDoc.file_type);
    formData.append("file", newDoc.file);

    try {
      await axios.post(`${API}/admin/prejoining/documents`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("Document uploaded");
      setNewDoc({ file_name: "", file_type: "offer_letter", file: null });
      fetchDocuments();
    } catch (err) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    
    try {
      await axios.delete(`${API}/admin/prejoining/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(documents.filter(d => d.id !== docId));
      toast.success("Document deleted");
    } catch (err) {
      toast.error("Failed to delete document");
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/admin/prejoining/documents/download/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const linkSource = `data:${res.data.content_type};base64,${res.data.file_data}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = res.data.original_filename || doc.file_name;
      downloadLink.click();
    } catch (err) {
      toast.error("Failed to download");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-navy text-white px-6 py-4 flex items-center justify-between">
          <h2 className="font-playfair text-xl">Documents - {candidate.name}</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Upload Form */}
          <form onSubmit={handleUpload} className="mb-6 pb-6 border-b border-gray-100">
            <h3 className="font-dm font-medium text-charcoal mb-4">Upload New Document</h3>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Document name (e.g., Offer Letter)"
                value={newDoc.file_name}
                onChange={(e) => setNewDoc({ ...newDoc, file_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
              />
              <select
                value={newDoc.file_type}
                onChange={(e) => setNewDoc({ ...newDoc, file_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
              >
                {DOCUMENT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <input
                type="file"
                onChange={(e) => setNewDoc({ ...newDoc, file: e.target.files[0] })}
                className="w-full text-sm"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              />
              <button
                type="submit"
                disabled={uploading}
                className="w-full bg-gold text-white py-2 rounded-lg font-dm text-sm hover:bg-gold/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload Document
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Documents List */}
          <div>
            <h3 className="font-dm font-medium text-charcoal mb-4">Uploaded Documents ({documents.length})</h3>
            {loading ? (
              <div className="text-center py-8">
                <div className="w-6 h-6 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto" />
              </div>
            ) : documents.length === 0 ? (
              <p className="text-center py-8 text-charcoal-muted font-dm text-sm">No documents uploaded yet</p>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-dm text-sm text-charcoal">{doc.file_name}</p>
                        <p className="font-dm text-xs text-gray-500 capitalize">{doc.file_type.replace(/_/g, ' ')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDownload(doc)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Pending Questions Modal
const PendingQuestionsModal = ({ questions, onClose, token, onReply }) => {
  const [replyText, setReplyText] = useState({});
  const [replying, setReplying] = useState({});

  const handleReply = async (questionId) => {
    if (!replyText[questionId]?.trim()) return;
    setReplying({ ...replying, [questionId]: true });
    
    try {
      await axios.post(`${API}/admin/prejoining/questions/${questionId}/reply`, 
        { reply: replyText[questionId] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Reply sent");
      setReplyText({ ...replyText, [questionId]: "" });
      onReply();
    } catch (err) {
      toast.error("Failed to send reply");
    } finally {
      setReplying({ ...replying, [questionId]: false });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-blue-600 text-white px-6 py-4 flex items-center justify-between">
          <h2 className="font-playfair text-xl flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Pending Questions ({questions.length})
          </h2>
          <button onClick={onClose} className="text-white/70 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {questions.length === 0 ? (
            <p className="text-center py-8 text-charcoal-muted font-dm">No pending questions</p>
          ) : (
            <div className="space-y-4">
              {questions.map((q) => (
                <div key={q.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-dm font-medium text-charcoal">{q.candidate_name}</p>
                      <p className="font-dm text-xs text-gray-500">{new Date(q.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <p className="font-dm text-sm text-charcoal mb-4 bg-gray-50 p-3 rounded">{q.question}</p>
                  
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Type your reply..."
                      value={replyText[q.id] || ""}
                      onChange={(e) => setReplyText({ ...replyText, [q.id]: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-blue-500"
                    />
                    <button
                      onClick={() => handleReply(q.id)}
                      disabled={replying[q.id]}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg font-dm text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {replying[q.id] ? "..." : <><Send className="w-4 h-4" /> Reply</>}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Offer Letter Upload Modal
const OfferLetterUploadModal = ({ candidate, onClose, token, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a PDF file");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/admin/prejoining/candidates/${candidate.id}/upload-offer-letter`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("Offer letter uploaded and notification sent!");
      onSuccess();
      onClose();
    } catch (err) {
      toast.error("Failed to upload offer letter");
    } finally {
      setUploading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-amber-600 text-white px-6 py-4 flex items-center justify-between">
          <h2 className="font-playfair text-xl">Upload Offer Letter</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <p className="font-dm text-sm text-gray-600 mb-4">
            Upload offer letter for <strong>{candidate?.name}</strong>. The candidate will receive an email notification.
          </p>
          
          <input
            type="file"
            accept=".pdf"
            onChange={(e) => setFile(e.target.files[0])}
            className="w-full mb-4"
          />
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg font-dm text-sm hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload & Notify
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Candidate Documents Review Component
const CandidateDocumentsReview = ({ token, pendingDocs, onUpdate }) => {
  const [allDocs, setAllDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState({});
  const [notes, setNotes] = useState({});

  useEffect(() => {
    fetchAllDocs();
  }, []);

  const fetchAllDocs = async () => {
    try {
      const res = await axios.get(`${API}/admin/prejoining/candidate-documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllDocs(res.data);
    } catch (err) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (docId, status) => {
    setReviewing({ ...reviewing, [docId]: true });
    try {
      await axios.post(`${API}/admin/prejoining/candidate-documents/${docId}/review`, 
        { status, admin_notes: notes[docId] || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(status === "approved" ? "Document approved" : "Document rejected");
      fetchAllDocs();
      onUpdate();
    } catch (err) {
      toast.error("Failed to review document");
    } finally {
      setReviewing({ ...reviewing, [docId]: false });
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/admin/prejoining/candidate-documents/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const linkSource = `data:${res.data.file_type};base64,${res.data.file_data}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = res.data.file_name;
      downloadLink.click();
    } catch (err) {
      toast.error("Failed to download");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;
  }

  const pendingDocsList = allDocs.filter(d => d.status === "pending");
  const reviewedDocs = allDocs.filter(d => d.status !== "pending");

  return (
    <div className="space-y-6">
      {/* Pending Documents */}
      <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
        <div className="bg-amber-50 px-6 py-4 border-b border-amber-100">
          <h3 className="font-playfair text-lg text-amber-800">Pending Review ({pendingDocsList.length})</h3>
        </div>
        {pendingDocsList.length === 0 ? (
          <p className="p-6 text-center text-gray-500 font-dm">No documents pending review</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {pendingDocsList.map((doc) => (
              <div key={doc.id} className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-dm font-medium text-charcoal">{doc.document_name}</p>
                    <p className="font-dm text-xs text-gray-500">{doc.candidate_name} • {doc.document_category}</p>
                    <p className="font-dm text-xs text-gray-400">{new Date(doc.uploaded_at).toLocaleString()}</p>
                  </div>
                  <button onClick={() => handleDownload(doc)} className="text-blue-600 hover:text-blue-800">
                    <Download className="w-5 h-5" />
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Add notes (optional)"
                  value={notes[doc.id] || ""}
                  onChange={(e) => setNotes({ ...notes, [doc.id]: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg font-dm text-sm mb-3"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => handleReview(doc.id, "approved")}
                    disabled={reviewing[doc.id]}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-dm text-sm hover:bg-green-700 disabled:opacity-50"
                  >
                    {reviewing[doc.id] ? "..." : "Approve"}
                  </button>
                  <button
                    onClick={() => handleReview(doc.id, "rejected")}
                    disabled={reviewing[doc.id]}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-dm text-sm hover:bg-red-700 disabled:opacity-50"
                  >
                    {reviewing[doc.id] ? "..." : "Reject"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reviewed Documents */}
      <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
          <h3 className="font-playfair text-lg text-charcoal">Reviewed Documents ({reviewedDocs.length})</h3>
        </div>
        {reviewedDocs.length === 0 ? (
          <p className="p-6 text-center text-gray-500 font-dm">No reviewed documents yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-dm text-xs text-gray-500">Document</th>
                  <th className="px-4 py-2 text-left font-dm text-xs text-gray-500">Candidate</th>
                  <th className="px-4 py-2 text-left font-dm text-xs text-gray-500">Status</th>
                  <th className="px-4 py-2 text-left font-dm text-xs text-gray-500">Reviewed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reviewedDocs.map((doc) => (
                  <tr key={doc.id}>
                    <td className="px-4 py-3 font-dm text-sm">{doc.document_name}</td>
                    <td className="px-4 py-3 font-dm text-sm text-gray-500">{doc.candidate_name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        doc.status === "approved" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      }`}>{doc.status}</span>
                    </td>
                    <td className="px-4 py-3 font-dm text-xs text-gray-500">{doc.reviewed_at ? new Date(doc.reviewed_at).toLocaleDateString() : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Email Templates Manager Component
const EmailTemplatesManager = ({ token }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data);
    } catch (err) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/email-templates/${editing.template_key}`, 
        { name: editing.name, subject: editing.subject, body: editing.body },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Template saved");
      setEditing(null);
      fetchTemplates();
    } catch (err) {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (templateKey) => {
    if (!window.confirm("Reset this template to default?")) return;
    try {
      await axios.post(`${API}/admin/email-templates/${templateKey}/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Template reset to default");
      fetchTemplates();
    } catch (err) {
      toast.error("Failed to reset template");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="font-dm text-sm text-blue-800">
          <strong>Available placeholders:</strong> {"{{candidate_name}}, {{candidate_email}}, {{position}}, {{salary}}, {{portal_url}}, {{access_code}}, {{otp}}, {{document_name}}, {{acceptance_timestamp}}"}
        </p>
      </div>

      {templates.map((template) => (
        <div key={template.template_key} className="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="font-dm font-medium text-charcoal">{template.name}</h3>
              <p className="font-dm text-xs text-gray-500">{template.template_key}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setEditing(template)}
                className="px-3 py-1 text-gold border border-gold rounded-lg font-dm text-sm hover:bg-gold/10"
              >
                Edit
              </button>
              {!template.is_default && (
                <button
                  onClick={() => handleReset(template.template_key)}
                  className="px-3 py-1 text-gray-500 border border-gray-200 rounded-lg font-dm text-sm hover:bg-gray-50"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
          <div className="px-6 py-4">
            <p className="font-dm text-sm text-gray-600"><strong>Subject:</strong> {template.subject}</p>
          </div>
        </div>
      ))}

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="bg-navy text-white px-6 py-4 flex items-center justify-between">
              <h2 className="font-playfair text-xl">Edit Template: {editing.name}</h2>
              <button onClick={() => setEditing(null)} className="text-white/70 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-150px)]">
              <div className="space-y-4">
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Template Name</label>
                  <input
                    type="text"
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm"
                  />
                </div>
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Subject Line</label>
                  <input
                    type="text"
                    value={editing.subject}
                    onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm"
                  />
                </div>
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Email Body (HTML)</label>
                  <textarea
                    value={editing.body}
                    onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                    rows={15}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-mono text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gold text-white rounded-lg font-dm text-sm hover:bg-gold/90 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Template"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreJoiningAdmin;
